import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useUserContext } from '@/hooks/useUserContext';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Minus, Activity, 
  Target, CheckCircle, Clock, AlertTriangle, Users,
  BarChart3, Award, ChevronRight, RefreshCw, Brain
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface WorkerIntervention {
  id: string;
  title: string;
  category: string;
  delivery_method: string;
  status: string;
  created_at: string;
  delivered_at: string | null;
  completed_at: string | null;
  risk_score_at_delivery: number | null;
  risk_score_after_30d: number | null;
  safety_state_at_delivery: string | null;
  safety_state_after_30d: string | null;
  effectiveness_score: number | null;
  effectiveness_evaluated_at: string | null;
}

interface WorkerWithResults {
  id: string;
  display_name: string;
  team: string | null;
  site_id: string | null;
  current_risk_score: number;
  safety_state: string;
  interventions: WorkerIntervention[];
  total_interventions: number;
  completed_interventions: number;
  avg_effectiveness: number | null;
  risk_trend: 'improving' | 'stable' | 'declining' | 'unknown';
}

interface AggregateStats {
  total_interventions: number;
  completed_interventions: number;
  avg_effectiveness: number | null;
  workers_improved: number;
  workers_declined: number;
  workers_stable: number;
}

interface WorkerResultsProps {
  siteId?: string | null;
}

export default function WorkerResults({ siteId: propSiteId }: WorkerResultsProps = {}) {
  const { workerId } = useParams<{ workerId?: string }>();
  const navigate = useNavigate();
  const { role, companyId: adminCompanyId } = useAdminAuth();
  const { companyId, isLoading: contextLoading } = useUserContext();
  
  const effectiveCompanyId = companyId || adminCompanyId;
  
  const [workers, setWorkers] = useState<WorkerWithResults[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<WorkerWithResults | null>(null);
  const [stats, setStats] = useState<AggregateStats>({
    total_interventions: 0,
    completed_interventions: 0,
    avg_effectiveness: null,
    workers_improved: 0,
    workers_declined: 0,
    workers_stable: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(propSiteId || 'all');
  const [activeTab, setActiveTab] = useState<'overview' | 'workers'>('overview');

  // If propSiteId changes, update selectedSite
  useEffect(() => {
    if (propSiteId) {
      setSelectedSite(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    if (!contextLoading && effectiveCompanyId) {
      fetchData();
    }
  }, [selectedSite, contextLoading, effectiveCompanyId]);

  useEffect(() => {
    if (workerId && workers.length > 0) {
      const worker = workers.find(w => w.id === workerId);
      if (worker) setSelectedWorker(worker);
    }
  }, [workerId, workers]);

  const fetchData = async () => {
    if (!effectiveCompanyId) return;
    
    setIsLoading(true);
    try {
      // Fetch workers with their interventions
      let workersQuery = supabase
        .from('worker_profiles')
        .select(`
          id,
          display_name,
          team,
          site_id,
          current_risk_score,
          safety_state
        `)
        .eq('company_id', effectiveCompanyId)
        .order('current_risk_score', { ascending: false });

      if (selectedSite !== 'all') {
        workersQuery = workersQuery.eq('site_id', selectedSite);
      }

      const { data: workersData, error: workersError } = await workersQuery;
      if (workersError) throw workersError;

      // Fetch all interventions for this company
      const { data: interventionsData, error: interventionsError } = await supabase
        .from('interventions')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .not('target_worker_id', 'is', null)
        .order('created_at', { ascending: false });

      if (interventionsError) throw interventionsError;

      // Group interventions by worker
      const interventionsByWorker: Record<string, WorkerIntervention[]> = {};
      (interventionsData || []).forEach((i: any) => {
        if (!interventionsByWorker[i.target_worker_id]) {
          interventionsByWorker[i.target_worker_id] = [];
        }
        interventionsByWorker[i.target_worker_id].push({
          id: i.id,
          title: i.title,
          category: i.category,
          delivery_method: i.delivery_method,
          status: i.status,
          created_at: i.created_at,
          delivered_at: i.delivered_at,
          completed_at: i.completed_at,
          risk_score_at_delivery: i.risk_score_at_delivery,
          risk_score_after_30d: i.risk_score_after_30d,
          safety_state_at_delivery: i.safety_state_at_delivery,
          safety_state_after_30d: i.safety_state_after_30d,
          effectiveness_score: i.effectiveness_score,
          effectiveness_evaluated_at: i.effectiveness_evaluated_at
        });
      });

      // Build worker results
      const workersWithResults: WorkerWithResults[] = (workersData || []).map((w: any) => {
        const workerInterventions = interventionsByWorker[w.id] || [];
        const completed = workerInterventions.filter(i => i.status === 'completed').length;
        
        // Calculate average effectiveness for evaluated interventions
        const evaluated = workerInterventions.filter(i => i.effectiveness_score !== null);
        const avgEffectiveness = evaluated.length > 0
          ? evaluated.reduce((sum, i) => sum + (i.effectiveness_score || 0), 0) / evaluated.length
          : null;

        // Determine risk trend from interventions with before/after scores
        let trend: 'improving' | 'stable' | 'declining' | 'unknown' = 'unknown';
        const withScores = workerInterventions.filter(i => 
          i.risk_score_at_delivery !== null && i.risk_score_after_30d !== null
        );
        if (withScores.length > 0) {
          const latestWithScores = withScores[0];
          const before = latestWithScores.risk_score_at_delivery || 0;
          const after = latestWithScores.risk_score_after_30d || 0;
          if (after < before - 5) trend = 'improving';
          else if (after > before + 5) trend = 'declining';
          else trend = 'stable';
        }

        return {
          id: w.id,
          display_name: w.display_name || 'Unknown',
          team: w.team,
          site_id: w.site_id,
          current_risk_score: w.current_risk_score || 0,
          safety_state: w.safety_state || 'safe',
          interventions: workerInterventions,
          total_interventions: workerInterventions.length,
          completed_interventions: completed,
          avg_effectiveness: avgEffectiveness,
          risk_trend: trend
        };
      }).filter(w => w.total_interventions > 0);

      // Calculate aggregate stats
      const allInterventions = Object.values(interventionsByWorker).flat();
      const completedInterventions = allInterventions.filter(i => i.status === 'completed');
      const allEvaluated = allInterventions.filter(i => i.effectiveness_score !== null);
      
      setStats({
        total_interventions: allInterventions.length,
        completed_interventions: completedInterventions.length,
        avg_effectiveness: allEvaluated.length > 0
          ? allEvaluated.reduce((sum, i) => sum + (i.effectiveness_score || 0), 0) / allEvaluated.length
          : null,
        workers_improved: workersWithResults.filter(w => w.risk_trend === 'improving').length,
        workers_declined: workersWithResults.filter(w => w.risk_trend === 'declining').length,
        workers_stable: workersWithResults.filter(w => w.risk_trend === 'stable').length
      });

      setWorkers(workersWithResults);
    } catch (error) {
      console.error('Error fetching worker results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSafetyStateBadge = (state: string) => {
    switch (state) {
      case 'chronic': return <Badge variant="destructive">Chronic</Badge>;
      case 'high_risk': return <Badge className="bg-orange-500/20 text-orange-600 dark:text-orange-400">High Risk</Badge>;
      case 'at_risk': return <Badge className="bg-warning/20 text-warning">At Risk</Badge>;
      case 'safe': return <Badge className="bg-success/20 text-success">Safe</Badge>;
      default: return <Badge variant="outline">{state}</Badge>;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingDown className="h-4 w-4 text-success" />;
      case 'declining': return <TrendingUp className="h-4 w-4 text-destructive" />;
      case 'stable': return <Minus className="h-4 w-4 text-muted-foreground" />;
      default: return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getEffectivenessColor = (score: number | null) => {
    if (score === null) return 'text-muted-foreground';
    if (score >= 20) return 'text-success';
    if (score >= 0) return 'text-warning';
    return 'text-destructive';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Worker Detail View
  if (selectedWorker) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setSelectedWorker(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{selectedWorker.display_name}</h2>
            <p className="text-muted-foreground">{selectedWorker.team || 'No team'}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {getSafetyStateBadge(selectedWorker.safety_state)}
            <Badge variant="outline" className="text-lg px-3 py-1">
              {Math.round(selectedWorker.current_risk_score)}% Risk
            </Badge>
          </div>
        </div>

        {/* Worker Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total Interventions</p>
              <p className="text-2xl font-bold">{selectedWorker.total_interventions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-success">{selectedWorker.completed_interventions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Avg Effectiveness</p>
              <p className={cn("text-2xl font-bold", getEffectivenessColor(selectedWorker.avg_effectiveness))}>
                {selectedWorker.avg_effectiveness !== null 
                  ? `${selectedWorker.avg_effectiveness.toFixed(1)}%` 
                  : 'N/A'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Risk Trend</p>
              <div className="flex items-center gap-2">
                {getTrendIcon(selectedWorker.risk_trend)}
                <span className="text-lg font-semibold capitalize">{selectedWorker.risk_trend}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Intervention Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Intervention History
            </CardTitle>
            <CardDescription>
              Timeline of all interventions delivered to this worker
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-4">
                {selectedWorker.interventions.map((intervention, idx) => (
                  <div 
                    key={intervention.id}
                    className={cn(
                      "relative pl-6 pb-4",
                      idx !== selectedWorker.interventions.length - 1 && "border-l-2 border-muted ml-2"
                    )}
                  >
                    {/* Timeline dot */}
                    <div className={cn(
                      "absolute left-0 top-0 w-4 h-4 rounded-full border-2 bg-background -translate-x-1/2",
                      intervention.status === 'completed' ? "border-success bg-success/20" :
                      intervention.status === 'pending' ? "border-warning bg-warning/20" :
                      "border-muted-foreground"
                    )} />

                    <Card className="ml-4">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold">{intervention.title}</h4>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Badge variant="outline" className="capitalize text-xs">
                                {intervention.category.replace(/_/g, ' ')}
                              </Badge>
                              <span>•</span>
                              <span>{format(new Date(intervention.created_at), 'MMM d, yyyy')}</span>
                            </div>
                          </div>
                          <Badge className={cn(
                            intervention.status === 'completed' ? "bg-success/20 text-success" :
                            intervention.status === 'pending' ? "bg-warning/20 text-warning" :
                            intervention.status === 'viewed' ? "bg-primary/20 text-primary" :
                            "bg-muted text-muted-foreground"
                          )}>
                            {intervention.status}
                          </Badge>
                        </div>

                        {/* Before/After Risk Scores */}
                        {(intervention.risk_score_at_delivery !== null || intervention.risk_score_after_30d !== null) && (
                          <div className="mt-3 p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground mb-2">Risk Score Impact</p>
                            <div className="flex items-center gap-4">
                              <div>
                                <span className="text-xs text-muted-foreground">Before</span>
                                <p className="text-lg font-bold">
                                  {intervention.risk_score_at_delivery?.toFixed(0) ?? 'N/A'}%
                                </p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <span className="text-xs text-muted-foreground">After 30d</span>
                                <p className={cn(
                                  "text-lg font-bold",
                                  intervention.risk_score_after_30d !== null && intervention.risk_score_at_delivery !== null
                                    ? intervention.risk_score_after_30d < intervention.risk_score_at_delivery
                                      ? "text-success"
                                      : intervention.risk_score_after_30d > intervention.risk_score_at_delivery
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                    : "text-muted-foreground"
                                )}>
                                  {intervention.risk_score_after_30d?.toFixed(0) ?? 'Pending'}%
                                </p>
                              </div>
                              {intervention.effectiveness_score !== null && (
                                <>
                                  <div className="border-l pl-4 ml-2">
                                    <span className="text-xs text-muted-foreground">Effectiveness</span>
                                    <p className={cn(
                                      "text-lg font-bold",
                                      getEffectivenessColor(intervention.effectiveness_score)
                                    )}>
                                      {intervention.effectiveness_score.toFixed(1)}%
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Safety State Change */}
                        {intervention.safety_state_at_delivery && (
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">State:</span>
                            {getSafetyStateBadge(intervention.safety_state_at_delivery)}
                            {intervention.safety_state_after_30d && (
                              <>
                                <ChevronRight className="h-4 w-4" />
                                {getSafetyStateBadge(intervention.safety_state_after_30d)}
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}

                {selectedWorker.interventions.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    No interventions recorded for this worker
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Overview View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Intervention Results
          </h2>
          <p className="text-muted-foreground">
            Track intervention effectiveness and worker outcomes
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Aggregate Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Interventions</p>
            <p className="text-3xl font-bold">{stats.total_interventions}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.completed_interventions} completed
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Avg Effectiveness</p>
            <p className={cn("text-3xl font-bold", getEffectivenessColor(stats.avg_effectiveness))}>
              {stats.avg_effectiveness !== null 
                ? `${stats.avg_effectiveness.toFixed(1)}%` 
                : 'N/A'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Risk reduction
            </p>
          </CardContent>
        </Card>
        <Card className="bg-success/5 border-success/20">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Workers Improved</p>
            <p className="text-3xl font-bold text-success">{stats.workers_improved}</p>
            <div className="flex items-center gap-1 text-xs text-success mt-1">
              <TrendingDown className="h-3 w-3" />
              Lower risk after intervention
            </div>
          </CardContent>
        </Card>
        <Card className="bg-destructive/5 border-destructive/20">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Workers Declined</p>
            <p className="text-3xl font-bold text-destructive">{stats.workers_declined}</p>
            <div className="flex items-center gap-1 text-xs text-destructive mt-1">
              <TrendingUp className="h-3 w-3" />
              Higher risk after intervention
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workers with Interventions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Workers with Interventions
          </CardTitle>
          <CardDescription>
            Click on a worker to see their full intervention history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {workers.map((worker) => (
                <div
                  key={worker.id}
                  className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedWorker(worker)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{worker.display_name}</span>
                      {getSafetyStateBadge(worker.safety_state)}
                    </div>
                    <p className="text-sm text-muted-foreground">{worker.team || 'No team'}</p>
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-bold">{worker.total_interventions}</p>
                      <p className="text-xs text-muted-foreground">Interventions</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-success">{worker.completed_interventions}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className={cn("font-bold", getEffectivenessColor(worker.avg_effectiveness))}>
                        {worker.avg_effectiveness !== null 
                          ? `${worker.avg_effectiveness.toFixed(0)}%` 
                          : 'N/A'}
                      </p>
                      <p className="text-xs text-muted-foreground">Effectiveness</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(worker.risk_trend)}
                      <span className="capitalize text-xs">{worker.risk_trend}</span>
                    </div>
                  </div>

                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              ))}

              {workers.length === 0 && (
                <div className="text-center text-muted-foreground py-12">
                  <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No intervention data available yet</p>
                  <p className="text-sm">Deliver interventions to workers to see results here</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
